// Deterministic RNG service.
// Exact mulberry32 implementation per GDD Appendix A §A.1.

export type RngStreamName = 'spawn' | 'loot' | 'ai' | 'crit' | 'vfx' | 'audio';

const STREAM_NAMES: RngStreamName[] = ['spawn', 'loot', 'ai', 'crit', 'vfx', 'audio'];

export class RngStream {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  next(): number {
    this.state = (this.state + 0x6D2B79F5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  next_u32(): number {
    return (this.next() * 4294967296) >>> 0;
  }

  next_int(n: number): number {
    return Math.floor(this.next() * n);
  }

  next_range(a: number, b: number): number {
    return a + this.next() * (b - a);
  }

  pick<T>(arr: readonly T[]): T {
    return arr[this.next_int(arr.length)];
  }

  getState(): number {
    return this.state >>> 0;
  }

  setState(s: number): void {
    this.state = s >>> 0;
  }
}

function fnv1a32(s: string): number {
  let h = 0x811C9DC5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i) & 0xFF;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export class RngService {
  private streams = new Map<RngStreamName, RngStream>();

  constructor(seed: number) {
    for (const name of STREAM_NAMES) {
      const streamSeed = (seed ^ fnv1a32(name)) >>> 0;
      this.streams.set(name, new RngStream(streamSeed));
    }
  }

  get(name: RngStreamName): RngStream {
    const s = this.streams.get(name);
    if (!s) throw new Error(`Unknown RNG stream: ${name}`);
    return s;
  }

  snapshot(): Record<RngStreamName, number> {
    const out = {} as Record<RngStreamName, number>;
    for (const name of STREAM_NAMES) {
      out[name] = this.get(name).getState();
    }
    return out;
  }

  restore(snapshot: Record<RngStreamName, number>): void {
    for (const name of STREAM_NAMES) {
      this.get(name).setState(snapshot[name]);
    }
  }
}
